import React from 'react';
import classnames from 'classnames';
import { Field } from 'signer-app/types/editor-types';
import {
  Origin,
  signatureRequestContext,
  zoomContext,
  ZoomContextShape,
} from 'signer-app/signature-request/context';
import { useSetFieldTooltipRef } from 'signer-app/signature-request/field-tooltip';
import { ORIGIN_PAGE } from 'signer-app/signature-request/constants';
import { positionContext } from 'signer-app/signature-request/position-context';
import DisplayField, {
  FieldContextProvider,
  WrapperStyles,
} from 'signer-app/signature-request/display-field';

import styles from 'signer-app/signature-request/handle.module.css';
import { useShouldShowDrawer } from 'signer-app/utils/use-should-show-drawer';
import { useLatestRef } from 'signer-app/utils/use-latest-ref';

type Props = {
  // The way I built this is super awkward. Signer and Overlay use DisplayField,
  // but the Editor uses a different component. I don't see a good way to make
  // the types cooperate here.
  Field: React.ComponentType<any>;
  fieldData: Field;
  origin?: Origin;
  showDrawer?: boolean;
};

type ContextData = {
  stableScrollFieldToCenter: () => void;
  isSelected: boolean;
  textScale: ZoomContextShape['textScale'];
  documentPreview: boolean;
  inlinePreviewMode: boolean;
  lastUpdatedFieldId?: Field['id'];
  x: string | number;
  y: string | number;
  width: string | number;
  height: string | number;
};

const SCROLL_DELAY = 300; // ms

function Handle({
  Field = DisplayField,
  fieldData,
  isSelected,
  textScale,
  documentPreview,
  inlinePreviewMode,
  lastUpdatedFieldId,
  x,
  y,
  width,
  height,
  showDrawer = false,
  stableScrollFieldToCenter,
}: Props & ContextData) {
  const rootSignerClass = `signer-${fieldData.signer}`;
  const signerClass = `bd-${rootSignerClass}`;

  const elementRef = React.useRef<HTMLDivElement>(null);
  useSetFieldTooltipRef(fieldData.id, elementRef);
  React.useLayoutEffect(() => {
    if (inlinePreviewMode && lastUpdatedFieldId === fieldData.id) {
      if (elementRef.current?.scrollIntoView) {
        elementRef.current.scrollIntoView();
      }
    }
  }, [fieldData.id, inlinePreviewMode, lastUpdatedFieldId]);

  /**
   * By using a stable callback, this is only going to trigger when the drawer is
   * active and the field becomes selected.
   */
  React.useLayoutEffect(() => {
    if (showDrawer && isSelected) {
      /**
       * This needs a small delay, so that the drawer can adjust its size before
       * scrolling the field into view.
       */
      const t = setTimeout(stableScrollFieldToCenter, SCROLL_DELAY);
      return () => clearTimeout(t);
    }
    return () => {};
  }, [showDrawer, isSelected, stableScrollFieldToCenter]);

  const handleStyle = {
    left: x,
    top: y,
    width,
    height,
  };

  const wrapperStyles = React.useMemo(
    (): WrapperStyles => ({
      position: 'absolute',
      height: `${100 / textScale}%`,
      width: `${100 / textScale}%`,
      transformOrigin: 'top left',
      transform: `scale(${textScale})`,
      // Safari requires the WebKit prefix
      WebkitTransformOrigin: 'top left',
      WebkitTransform: `scale(${textScale})`,
    }),
    [textScale],
  );

  return (
    <div
      ref={elementRef}
      data-field={fieldData.name || fieldData.id}
      data-field-id={fieldData.id}
      data-qa-ref={`field-${fieldData.type}`}
      data-testid={`field-${fieldData.type}`}
      data-signer={fieldData.signer}
      className={classnames(styles.handle, styles[`${signerClass}`], {
        [styles[fieldData.type]]: true,
        [styles.selected]: isSelected,
        [styles.documentPreview]: documentPreview,
        // This class adds pointer-events: none so we need to exclude hyperlinks to make them work
        [styles.readOnly]: fieldData.readOnly && fieldData.type !== 'hyperlink',
      })}
      style={handleStyle}
      data-field-data={
        typeof jest === 'undefined' ? undefined : JSON.stringify(fieldData)
      }
    >
      <FieldContextProvider fieldData={fieldData} wrapperStyles={wrapperStyles}>
        <Field
          fieldData={fieldData}
          textScale={textScale}
          disabled={documentPreview && fieldData.signer === 'preparer'}
          height={wrapperStyles.height}
          width={wrapperStyles.width}
        />
      </FieldContextProvider>
    </div>
  );
}

const MemoHandle = React.memo(Handle);

export default function HandleContext(props: Props) {
  const {
    selectedFieldIds,
    documentPreview,
    inlinePreviewMode,
    lastUpdatedFieldId,
  } = React.useContext(signatureRequestContext);
  const { textScale, scrollFieldToCenter } = React.useContext(zoomContext);
  const { toScreenCoords } = React.useContext(positionContext);
  const { fieldData, origin = ORIGIN_PAGE } = props;
  const { x, y, width, height } = React.useMemo(
    () => toScreenCoords(fieldData, origin),
    [fieldData, origin, toScreenCoords],
  );
  const showDrawer = useShouldShowDrawer();

  const fieldDataRef = useLatestRef(fieldData);
  /**
   * By using a ref to pass the field data, this function can be created exactly once,
   * and any time its run it will get the correct data to pass to the callback.
   */
  const stableScrollFieldToCenter = React.useCallback(() => {
    return scrollFieldToCenter(fieldDataRef.current);
  }, [fieldDataRef, scrollFieldToCenter]);

  return (
    <MemoHandle
      {...props}
      lastUpdatedFieldId={lastUpdatedFieldId}
      isSelected={selectedFieldIds.includes(props.fieldData.id)}
      textScale={textScale}
      showDrawer={showDrawer}
      stableScrollFieldToCenter={stableScrollFieldToCenter}
      x={x}
      y={y}
      width={width}
      height={height}
      documentPreview={documentPreview}
      inlinePreviewMode={inlinePreviewMode}
    />
  );
}
